import { memo } from "react";
import { cn } from "../../../../../lib/cn";
import { useIsMobile } from "../../../../../hooks/useIsMobile";
import {
  useDraftFlowProgressContext,
  useDraftFlowActions,
} from "../../../../../components/AIChat/DraftFlowProvider";
import { useDraftFlowStore } from "../../../../../stores/draftFlowStore";
import { CustomModal } from "../../../../../components/shared";
import { CloseIcon } from "../../../../../assets/icons";
import { ContractPanelContent } from "./ContractPanelContent";

export const ContractPanel = memo(({
  contracts,
  selectedContractId,
  contractDetail,
  contractLoading,
}) => {
  const { contractState } = useDraftFlowProgressContext();
  const { handleResizeStart } = useDraftFlowActions();
  const {
    showContractPanel,
    contractPanelWidth,
    activeSessionId,
    intentData,
    setShowContractPanel,
    setContractPanelWidth,
  } = useDraftFlowStore();

  const isMobile = useIsMobile();

  // Collapse the panel entirely until a session is active —
  // the consolidated chat empty state owns the no-session messaging.
  if (!activeSessionId) {
    return null;
  }

  const contentProps = {
    contracts,
    selectedContractId,
    contractDetail,
    contractLoading,
    contractState,
    intentData,
    activeSessionId,
  };

  if (isMobile) {
    return (
      <CustomModal
        title="Contract"
        open={showContractPanel}
        close={() => setShowContractPanel(false)}
        showPrimaryButton={false}
        showSecondaryButton={false}
        isDismissable
        dialogSize="lg"
      >
        <div className="h-[70vh] flex flex-col">
          <ContractPanelContent {...contentProps} />
        </div>
      </CustomModal>
    );
  }

  return (
    <>
      {showContractPanel && (
        <div
          role="slider"
          tabIndex={0}
          aria-valuenow={contractPanelWidth}
          aria-valuemin={240}
          aria-valuemax={600}
          aria-orientation="horizontal"
          aria-valuetext={`${contractPanelWidth} pixels`}
          aria-label="Resize contract panel"
          className="w-4 -mx-2 flex items-center justify-center cursor-col-resize z-10 group sticky top-4 self-start"
          style={{ height: "100%" }}
          onMouseDown={handleResizeStart}
          onKeyDown={(e) => {
            const step = e.shiftKey ? 50 : 10;
            if (e.key === "ArrowLeft") {
              e.preventDefault();
              setContractPanelWidth(contractPanelWidth - step);
            }
            if (e.key === "ArrowRight") {
              e.preventDefault();
              setContractPanelWidth(contractPanelWidth + step);
            }
          }}
          title="Drag to resize"
        >
          <div className="w-1 h-12 rounded-full bg-surface-disabled group-hover:bg-primary transition-colors" />
        </div>
      )}
      <div
        className={cn(
          "bg-white border-l flex flex-col shrink-0 transition-all duration-200 h-full",
          showContractPanel
            ? "opacity-100 translate-x-0"
            : "w-0 opacity-0 translate-x-10 p-0 border-0 overflow-hidden",
        )}
        style={{
          width: showContractPanel ? contractPanelWidth : 0,
        }}
      >
        <div
          className={cn(
            "p-4 flex flex-col h-full",
            !showContractPanel && "hidden",
          )}
        >
          <div className="border-b border-surface-border pb-3 mb-3">
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold text-primary text-balance">
                Contract
              </p>
              <button
                type="button"
                onClick={() => setShowContractPanel(false)}
                className="p-1.5 rounded-md hover:bg-surface-hover text-mediumgrey hover:text-darkgrey transition-colors cursor-pointer"
                aria-label="Close contract panel"
                title="Close"
              >
                <CloseIcon width={14} height={14} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-surface/50 rounded-xl border border-surface-border p-3">
            <ContractPanelContent {...contentProps} />
          </div>
        </div>
      </div>
    </>
  );
});

ContractPanel.displayName = "ContractPanel";
